import type {
	AssetSheetDerivedRowStatus,
	AssetSheetProgress,
	AssetSheetRowReportDTO,
	PaginatedResponse,
} from '@atlas/types'
import { apiClient } from './api.client'

/** What the Rows tab narrows the rows by; every field but `page` is optional. */
export interface AssetSheetRowFilters {
	tab?: string
	status?: AssetSheetDerivedRowStatus
	/** Part of an asset code, matched ignoring case. */
	assetCode?: string
	page: number
}

/** Rows per page of the Rows tab. */
export const ASSET_SHEET_ROWS_PAGE_SIZE = 50

/** ADMIN-only asset sheet sync. */
export const assetSheetApi = {
	/** Start a sync in the background; one already running makes it a no-op. */
	startSync: (): Promise<void> => apiClient.post('/admin/asset-sheet/sync').then(() => undefined),

	getProgress: (): Promise<AssetSheetProgress> =>
		apiClient.get<{ data: AssetSheetProgress }>('/admin/asset-sheet/progress').then((r) => r.data.data),

	/** A page of rows with their derived status, their assets' imports and the tags each asset gets. */
	listRows: ({
		tab,
		status,
		assetCode,
		page,
	}: AssetSheetRowFilters): Promise<PaginatedResponse<AssetSheetRowReportDTO>> =>
		apiClient
			.get<PaginatedResponse<AssetSheetRowReportDTO>>('/admin/asset-sheet/rows', {
				params: {
					...(tab && { 'filters[tab]': tab }),
					...(status && { 'filters[status]': status }),
					...(assetCode && { 'filters[assetCode]': assetCode }),
					limit: ASSET_SHEET_ROWS_PAGE_SIZE,
					page,
				},
			})
			.then((r) => r.data),

	/** Queue an asset's finally failed files again. */
	retryAsset: (assetCode: string): Promise<void> =>
		apiClient.post(`/admin/asset-sheet/assets/${encodeURIComponent(assetCode)}/retry`).then(() => undefined),
}
